import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';

import { queryClient } from '../services/react-query/react-query.client';
import { store } from '../store';
import {
    v2SettingsApi,
    ResolvedSetting,
    EffectiveResponse,
} from '../pages/app/pom-master-settings/v2-settings-api';

/**
 * Canonical v2 setting names the web app reads at runtime. These come from the
 * shared settings catalog (pom-2-api / node-api `catalog.json`) via each
 * setting's legacyAliases. Reads go through the v2 resolver (GET
 * /v2/settings/me) instead of the raw `customer.settings` doc so overrides,
 * locks, and the system→customer→group→team→user cascade are all honored.
 */
export const SETTING = {
    activityStatusFilters: 'safetyHub.maps.statusFilters.enabled',
    appointmentsCheckOnMeTracking: 'safetyHub.appointments.checkOnMeTracking.enabled',
    assetManagement: 'safetyHub.device.assetManagement.enabled',
    hideLastPing: 'safetyHub.usersAndDevices.hideLastPing',
    userUniqueIdentifier: 'app.identity.userIdentifier.mode',
    showMessageCenter: 'safetyHub.messageCenter.enabled',
    showInsightsDashboard: 'safetyHub.insightsDashboard.enabled',
    showRiskAssessment: 'safetyHub.safetyReport.enhancedToIntakeRiskAssessment.enabled',
    showGroupsTeamsDashboard: 'safetyHub.activityDashboard.groupsTeams.enabled',
    showRiskAssessmentAddressOnly: 'safetyHub.safetyReport.enhancedToIntakeRiskAssessment.addressOnly',
    isSafetyReportEnabled: 'safetyHub.safetyReport.enabled',
    isActivityReportEnabled: 'safetyHub.reports.activity.enabled',
    programReport: 'safetyHub.reports.programReport.enabled',
    mapSettingsDeviceEnabled: 'safetyHub.maps.devices.enabled',
    checkOnMeRiskReportSurvey: 'checkOnMe.riskReportSurvey.enabled',
    showTestMode: 'testMode.safetyHub.enabled',
    requireAdminApprovalForDeletion: 'app.accountDeletion.requireAdminApproval',
    organizationType: 'organization.type',
    showStatewideSearch: 'safetyHub.safetyReport.statewideSearch.enabled',
    chatSupportMode: 'chatSupport.mode',
    notificationRules: 'notifications.rules.enabled',
    iraSearchHistory: 'intakeRiskAssessment.searchHistory.enabled',
    iraFilteredCharges: 'intakeRiskAssessment.filteredCharges.enabled',
} as const;

export const EFFECTIVE_SETTINGS_QUERY_KEY = 'v2-effective-me';

const STALE_MS = 5 * 60 * 1000;
const MAX_TRANSIENT_RETRIES = 2;
const RETRY_DELAY_MS = 1000;
const CACHE_MS = 60 * 60 * 1000;

type Resolved = Record<string, ResolvedSetting>;

function pick<T>(resolved: Resolved, canonicalName: string, fallback: T): T {
    const entry = resolved[canonicalName];
    // The resolver always returns a value for a catalogued setting (system
    // default at minimum), so `fallback` only applies before the fetch
    // resolves or for a name that isn't in the catalog.
    return entry !== undefined && entry.value !== undefined ? (entry.value as T) : fallback;
}

/**
 * React hook: the effective settings for the current web-app user's own org,
 * resolved server-side. `getSetting(SETTING.x, fallback)` reads a value.
 */
export function useEffectiveSettings() {
    // Never fetch without a token. The unauthenticated /login shell renders
    // this hook too (AppRoutes), and an anonymous 401 would count as a
    // settled fetch and switch off the initial-load gate below for the login
    // that follows. Read from the store directly: callers re-render on auth
    // changes already (AppRoutes selects auth; DashboardLayout mounts after
    // login), and this keeps the hook usable without a redux Provider.
    const hasToken = !!store.getState().auth?.token;

    const query = useQuery<EffectiveResponse>(
        [EFFECTIVE_SETTINGS_QUERY_KEY],
        () => v2SettingsApi.getMine(),
        {
            staleTime: STALE_MS,
            cacheTime: CACHE_MS,
            refetchOnWindowFocus: true,
            enabled: hasToken,
            // Absorb a transient failure (5xx, network) inside the initial
            // loading state so the gate below stays closed instead of
            // falling through to defaults on one blip. A 401 is final: the
            // session is dead and the 401 listener logs the user out.
            retry: (failureCount, error: any) =>
                error?.response?.status !== 401 && failureCount < MAX_TRANSIENT_RETRIES,
            retryDelay: RETRY_DELAY_MS,
        },
    );

    // `isLoading` means the first fetch only (including its retries). Once a
    // fetch has failed for good the hook stays settled and consumers render
    // with catalog defaults, as they did before the gates existed. react-query v3 flips a never-succeeded query back to
    // status 'loading' on every refetch (a new observer mounting, window
    // focus). Consumers that hold rendering on isLoading (DashboardLayout,
    // the route gates) would otherwise unmount their children; the children
    // remount, observe this query again, trigger a refetch, and the shell
    // unmounts again. With /v2/settings/me returning 401 that ran unbounded
    // (2026-09-08: ~30 req/s from one stale tab until the WAF rate limit
    // blocked the client).
    const isInitialLoading = query.isLoading && query.errorUpdateCount === 0;

    // Memoize so `resolved`/`getSetting` keep a stable reference across renders
    // (only changing when the fetched data does), safe for consumers that put
    // them in dependency arrays or memo inputs.
    const resolved: Resolved = useMemo(() => query.data?.resolved ?? {}, [query.data]);

    const getSetting = useCallback(
        <T>(canonicalName: string, fallback: T): T => pick<T>(resolved, canonicalName, fallback),
        [resolved],
    );

    return { ...query, isLoading: isInitialLoading, resolved, getSetting };
}

/**
 * Imperative accessor for non-component code (services / store thunks) that
 * cannot use the hook. Reads through the shared react-query cache so it reuses
 * the same fetched result within the stale window.
 */
export async function getEffectiveSetting<T>(canonicalName: string, fallback: T): Promise<T> {
    try {
        const data = await queryClient.fetchQuery<EffectiveResponse>(
            [EFFECTIVE_SETTINGS_QUERY_KEY],
            () => v2SettingsApi.getMine(),
            { staleTime: STALE_MS, cacheTime: CACHE_MS },
        );
        return pick<T>(data?.resolved ?? {}, canonicalName, fallback);
    } catch {
        // Degrade to the fallback on any fetch failure, mirroring the hook. A
        // settings-fetch outage must never break a caller's flow (e.g. the
        // group/team invite that awaits this) — previously this was a
        // synchronous redux read that could not throw.
        return fallback;
    }
}
